import React, { useState, useEffect, useCallback } from 'react';
import { LayoutGrid, Eye, EyeOff, ChevronDown, ChevronUp, RotateCcw, X } from 'lucide-react';

const BRAND = '#F5831F';
const MUTED = '#5B5348';
const STORAGE_KEY = 'hibff_dashboard_layout';

/**
 * DashboardCustomizer — Manages section visibility and collapse state.
 *
 * Usage:
 *   const { sectionProps, CustomizerPanel, CustomizerButton } = useDashboardCustomizer('security', SECTIONS);
 *   // In JSX: {sectionProps('score').visible && <div {...sectionProps('score').collapseProps}>...</div>}
 */

const DEFAULT_SECTIONS = {};

export function useDashboardCustomizer(dashboardId, sectionDefs = []) {
  const storageKey = `${STORAGE_KEY}_${dashboardId}`;

  const getDefaults = useCallback(() => {
    const defaults = {};
    sectionDefs.forEach((s) => {
      defaults[s.id] = { visible: true, collapsed: false, order: s.order || 0 };
    });
    return defaults;
  }, [sectionDefs]);

  const [layout, setLayout] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with defaults so new sections appear
        const defaults = {};
        sectionDefs.forEach((s) => {
          defaults[s.id] = { visible: true, collapsed: false, order: s.order || 0, ...parsed[s.id] };
        });
        return defaults;
      }
    } catch (e) { /* ignore */ }
    return getDefaults();
  });

  const [showPanel, setShowPanel] = useState(false);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(layout));
  }, [layout, storageKey]);

  const toggleVisible = (id) => {
    setLayout((prev) => ({ ...prev, [id]: { ...prev[id], visible: !prev[id]?.visible } }));
  };

  const toggleCollapsed = (id) => {
    setLayout((prev) => ({ ...prev, [id]: { ...prev[id], collapsed: !prev[id]?.collapsed } }));
  };

  const resetLayout = () => {
    setLayout(getDefaults());
    localStorage.removeItem(storageKey);
  };

  const sectionProps = (id) => {
    const state = layout[id] || { visible: true, collapsed: false };
    const def = sectionDefs.find((s) => s.id === id);
    return {
      visible: state.visible,
      collapsed: state.collapsed,
      toggle: () => toggleCollapsed(id),
      label: def?.label || id,
      collapseProps: {
        'data-testid': `section-${id}`,
      },
      CollapseHeader: ({ children }) => (
        <button
          onClick={() => toggleCollapsed(id)}
          className="flex items-center gap-1.5 mb-1 group"
          data-testid={`collapse-${id}`}
        >
          {state.collapsed
            ? <ChevronDown size={14} style={{ color: MUTED }} />
            : <ChevronUp size={14} style={{ color: MUTED }} />}
          {children}
        </button>
      ),
    };
  };

  const CustomizerButton = () => (
    <button
      onClick={() => setShowPanel(!showPanel)}
      className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors"
      style={{ backgroundColor: showPanel ? `${BRAND}12` : '#F5F5F5', color: showPanel ? BRAND : MUTED }}
      data-testid="customizer-btn"
    >
      <LayoutGrid size={14} /> Layout
    </button>
  );

  const CustomizerPanel = () =>
    showPanel ? (
      <div
        className="bg-white rounded-lg mb-6 overflow-hidden"
        style={{ border: '1px solid #E0E0E0' }}
        data-testid="customizer-panel"
      >
        <div
          className="flex items-center justify-between px-4 py-2.5 border-b"
          style={{ borderColor: '#E0E0E0', backgroundColor: '#FAFAFA' }}
        >
          <div className="flex items-center gap-2">
            <LayoutGrid size={14} style={{ color: BRAND }} />
            <p className="text-sm font-bold" style={{ color: '#1A1209' }}>Dashboard Layout</p>
            <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F5F5F5', color: MUTED }}>
              {sectionDefs.filter((s) => layout[s.id]?.visible).length}/{sectionDefs.length} visible
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={resetLayout}
              className="text-[10px] px-2 py-1 rounded flex items-center gap-1"
              style={{ color: MUTED }}
              data-testid="reset-layout-btn"
            >
              <RotateCcw size={10} /> Reset
            </button>
            <button onClick={() => setShowPanel(false)}>
              <X size={14} style={{ color: MUTED }} />
            </button>
          </div>
        </div>
        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {sectionDefs.map((s) => {
            const state = layout[s.id] || { visible: true };
            return (
              <button
                key={s.id}
                onClick={() => toggleVisible(s.id)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] font-medium transition-colors text-left"
                style={{
                  backgroundColor: state.visible ? `${BRAND}08` : '#F5F5F5',
                  border: `1px solid ${state.visible ? BRAND : '#E0E0E0'}`,
                  color: state.visible ? '#1A1209' : MUTED,
                }}
                data-testid={`layout-toggle-${s.id}`}
              >
                {state.visible ? <Eye size={12} style={{ color: BRAND }} /> : <EyeOff size={12} />}
                {s.label}
              </button>
            );
          })}
        </div>
      </div>
    ) : null;

  return { sectionProps, CustomizerPanel, CustomizerButton, layout, resetLayout };
}
